import { useEffect, useState, useReducer, useMemo, useCallback, useContext } from 'react';
import {
  AgxRow,
  AgxColumn,
  AgxButton,
  AgxBodyText,
  AgxLabel,
  AgxSwitch,
  AgxToast,
  filterPageHideCondition,
  DocumentTypes,
  FormPage,
  useDeepMemo,
  AgxMultiOptionButton,
  DocumentTypesMap,
  AgxIconCircularGreenTick,
  AgxIconCircularIncomplete,
  AgxIconCircularWarning,
  cleanFullAddress,
  PropertyAddress,
} from '@urbanx/agx-ui-components';
import { useNavigate, useParams } from 'react-router-dom';
import { isEqual } from 'lodash-es';
import slugify from 'slugify';
import { ContentLayout, PageLayout } from 'layout';
import { Feature } from '@urbanx/agx.agencies.client';
import { MenuTitles, Pages } from 'constants/menu';

import { FormValue, FormValueType } from 'types/formConfig';
import { AgxToastState } from 'types/commonTypes';
import ErrorModal from 'helpers/ErrorModal';
import { PdfProcessedEvent } from 'types/pushNotifications';
import { FormPrompt } from 'components/FormPrompt';
import { useFeatureToggles, useFormConfig, useSelectedAgency, useSelectedCampaign } from 'hooks';
import { openFileInNewTab } from 'utils/openInNewTab';
import blankContractReducer, { ActionType } from './blankContractReducer';
import SetBlankContractConfirmModal from './SetBlankContractConfirmModal';
import './ManageCampaign.scss';
import { AuthContext } from '@urbanx/agx.auth.client';
import { CampaignDetailModelState, FormType } from '@urbanx/agx.campaigns.client';
import { usePdfProcessedEvent } from 'hooks/usePdfProcessedEvent';
import PanelElementRenderer from './Components/PanelElementRenderer';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxAnimatedToggleLoaderState,
  AgxDivider,
  AgxSpacer,
} from '@urbanx/agx.ui-components2';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import WithdrawListingAction from '../WithdrawListingAction';
import { getRexFieldChangeChecks, RexDisconnectedModalFields } from './RexDataUpdateHelper';
import useRexDisconnectedModal from './useRexDisconnectedModal';
import { isPropertyInformationPageAvailable } from './propertyInformationAvailability';
import useBlankContractGenerationStatus from './useBlankContractGenerationStatus';
import { SellerDisclosureModalValues } from 'components/sellers-disclosure-upload/SellerDisclosureModal';

interface Props {
  formType: FormType;
}

const ManageCampaign = (props: Props) => {
  const { formType } = props;
  const { agencyId, campaignId } = useParams();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureToggles();
  const { agency: selectedAgency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const {
    formConfig,
    isLoading: isLoadingFormConfig,
    saveForm,
    submitForm,
  } = useFormConfig(formType);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const { getAuthToken, userAccount } = useContext(AuthContext);
  const [validate, setValidate] = useState(false);
  const [errorContainerNames, setErrorContainerNames] = useState<string[]>([]);
  const [notifyAgent, setNotifyAgent] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([]);
  const [blankContractState, dispatch] = useReducer(blankContractReducer, {
    initialized: false,
  });
  const {
    addChangedField,
    modal: rexDisconnectedModal,
    openModal: openRexDisconnectedModal,
  } = useRexDisconnectedModal();
  const formValues = blankContractState as FormValue;

  const showQldInfo = campaign?.state === CampaignDetailModelState.QLD;
  const campaignIsValid = isPropertyInformationPageAvailable(campaign?.stage);

  useEffect(() => {
    if (agencyId && campaignId && campaign?.state === CampaignDetailModelState.NSW) {
      navigate(
        `/${Pages.AGENCIES}/${agencyId}/${Pages.CAMPAIGNS}/${campaignId}/${Pages.PROPERTY_INFORMATION_SECTION}`,
        { replace: true }
      );
    }
  }, [agencyId, campaign?.state, campaignId, navigate]);

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  // Prepopulate form values with existing data
  useEffect(() => {
    if (!isLoadingFormConfig && !!campaign?.campaignId && !!formConfig) {
      dispatch({
        type: ActionType.INIT,
        payload: formConfig.existingData,
      });
    }
  }, [isLoadingFormConfig, campaign?.campaignId, formConfig?.existingData]);

  useEffect(() => {
    if (blankContractState.initialized) {
      setTimeout(() => {
        setIsInitialRender(false);
      }, 500);
    }
  }, [blankContractState.initialized]);

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const alertHungPdf = useCallback(() => {
    updateToastState({
      color: 'info',
      message: 'Blank contract is still processing. You will be notified when it completes.',
      open: true,
    });
  }, []);
  const blankContractGeneration = useBlankContractGenerationStatus(alertHungPdf);
  const blankContractLoaderState = blankContractGeneration.loaderState;
  const isGeneratingBlankContract = blankContractGeneration.isBlocking;

  const validatePage = async () => {
    setValidate(true);
    await delay(100);

    const errorElements = document.getElementsByClassName('error');
    const errorContainers: string[] = [];
    pages.forEach((page) => {
      const container = document.getElementById(slugify(page.name));
      if (container) {
        const errorInContainers = container.getElementsByClassName('error');
        if (errorInContainers && errorInContainers.length > 0) {
          errorContainers.push(page.name);
        }
        const actionAlertsInContainers = container.getElementsByClassName('actionAlertContainer');
        const infoAlertsInContainers = container.getElementsByClassName(
          'informationAlertContainer'
        );
        if (
          (actionAlertsInContainers && actionAlertsInContainers.length > 0) ||
          (infoAlertsInContainers && infoAlertsInContainers.length > 0)
        ) {
          errorContainers.push(page.name);
        }
      }
    });
    setErrorContainerNames((prev) => (isEqual(prev, errorContainers) ? prev : errorContainers));
    return errorElements && errorElements.length > 0 ? false : true;
  };

  const validateRexRelatedFields = (
    id: string,
    value: FormValueType[keyof FormValueType],
    prevFormValues: FormValue
  ) => {
    const checks = getRexFieldChangeChecks(id, value, prevFormValues);
    checks.forEach(({ changed, field, value }) => {
      if (changed) {
        addChangedField(field, value);
      }
    });
  };

  const toggleLiveBlankContract = async (isSwitchLive: boolean) => {
    if (!campaignIsValid || isLoadingFormConfig) return;
    if (isSwitchLive) {
      setOpenConfirmationModal(true);
    } else {
      onSubmit();
    }
  };

  const onSaveFormValues = async () => {
    if (!campaign?.state || isLoadingFormConfig) return;

    if (campaignIsValid) {
      if (showQldInfo) {
        blankContractGeneration.start();
      }

      saveForm(formValues, [], {
        onSuccess: (result) => {
          setIsFormDirty(false);
          setNotifyAgent(false);

          if (result?.blankContractIsLive) {
            updateToastState({
              color: 'success',
              message: isEnabled(Feature.SubmitListingToRex)
                ? 'Blank contract is saved live, and sent to REX'
                : 'Blank Contract is saved live',
              open: true,
            });
          } else {
            updateToastState({
              color: 'success',
              message: 'Blank contract saved and is offline',
              open: true,
            });
          }

          validatePage();
        },
        onError: () => {
          blankContractGeneration.fail();
          updateToastState({
            color: 'error',
            message: 'Error saving campaign',
            open: true,
          });
        },
      });
    } else {
      updateToastState({
        color: 'error',
        message: 'Campaign is not ready for blank contract generation',
        open: true,
      });
    }
  };

  const onSubmit = async () => {
    if (!campaign?.state || !campaignIsValid || isLoadingFormConfig) return;

    await validatePage();

    if (showQldInfo) {
      blankContractGeneration.start();
    }

    submitForm(formValues, notifyAgent, {
      onSuccess: async (data) => {
        if ((data?.errors?.length ?? 0) > 0) {
          setSubmitFormErrors(data?.errors ?? []);
          setOpenErrorModal(true);
          setNotifyAgent(false);
          blankContractGeneration.fail();
        } else {
          setIsFormDirty(false);
        }

        if (data?.blankContractIsLive) {
          updateToastState({
            color: 'success',
            message: isEnabled(Feature.SubmitListingToRex)
              ? 'Blank contract is saved live, and sent to REX'
              : 'Blank Contract is saved live',
            open: true,
          });
        } else {
          updateToastState({
            color: 'success',
            message: 'Blank contract is offline',
            open: true,
          });
        }
      },
      onError: () => {
        blankContractGeneration.fail();
        updateToastState({
          color: 'error',
          message: 'Error generating blank contract',
          open: true,
        });
      },
    });
  };

  const onChange = (change: { id: string; value: FormValueType[keyof FormValueType] }) => {
    const { id, value } = change;

    if (
      id === 'CustomSpecialConditions' ||
      (formValues && !isEqual(formValues[id], change.value))
    ) {
      if (!isInitialRender) {
        setIsFormDirty(true);
        validateRexRelatedFields(id, value, formValues);
      }
      dispatch({ type: ActionType.UPDATE, payload: value, key: id });
    }
  };

  const onSellerDisclosureComplete = async (values: SellerDisclosureModalValues) => {
    const changes: Partial<FormValue> = {
      SellersDisclosure: values.sellersDisclosure,
      SurveyPlan: values.surveyPlan,
      PoolSafetyFileType: values.poolSafetyFileType,
      PoolSafetyAttachedToSellerDisclosure: values.poolSafetyAttachedToSellerDisclosure,
      PoolSafetyCertificatePDF: values.poolSafetyCertificatePDF,
      BuildingCertificatePDF: values.buildingCertificatePDF,
      PoolSafetyForm36PDF: values.poolSafetyForm36PDF,
      CommunityTitlesSchemeDetails: values.communityTitlesSchemeDetails,
      BodyCorporateDetails: values.bodyCorporateDetails,
    };
    const nextFormValues = { ...formValues, ...changes };

    await new Promise<void>((resolve, reject) => {
      saveForm(nextFormValues, [], {
        onSuccess: () => {
          Object.entries(changes).forEach(([key, value]) => {
            dispatch({
              type: ActionType.UPDATE,
              key,
              payload: value as FormValueType[keyof FormValueType],
            });
          });
          setIsFormDirty(false);
          updateToastState({
            color: 'success',
            message: 'Seller Disclosure saved successfully',
            open: true,
          });
          resolve();
        },
        onError: reject,
      });
    });
  };

  const pages = useDeepMemo<FormPage[]>(() => {
    return filterPageHideCondition(formConfig?.pages ?? [], formValues);
  }, [campaign?.campaignId, formValues, formConfig?.pages]);

  const pdfProcessed = (event: PdfProcessedEvent) => {
    if (event.campaignId !== campaignId) return;

    if (event.uploadedDocument.documentType === DocumentTypes.BlankSalesContract) {
      blankContractGeneration.complete();
    }
  };

  usePdfProcessedEvent(pdfProcessed);

  useEffect(() => {
    validatePage();
  }, [formValues, pages]);

  const fileDownloadOptions = useMemo(() => {
    if (!campaign?.blankSalesContract) return [];
    const options = [];

    if (campaign?.blankSalesContract) {
      options.push({
        text: DocumentTypesMap.BlankSalesContract,
        onClick: async () => {
          await openFileInNewTab(getAuthToken, campaign.blankSalesContract?.containerFilePath);
        },
      });
    }

    if (campaign?.blankSalesContractFlattened) {
      options.push({
        text: DocumentTypesMap.BlankSalesContractFlattened,
        onClick: async () => {
          await openFileInNewTab(
            getAuthToken,
            campaign.blankSalesContractFlattened?.containerFilePath
          );
        },
      });
    }

    return options;
  }, [campaign?.blankSalesContract, campaign?.blankSalesContractFlattened, getAuthToken]);

  const onNotifyAgentToggle = (checked: boolean) => {
    setNotifyAgent(checked);
  };

  const handleBlankContractLoaderComplete = () => {
    blankContractGeneration.finishAnimation();
    setIsFormDirty(false);
    setNotifyAgent(false);
    updateToastState({
      color: 'success',
      message: 'Blank contract generated',
      open: true,
    });
  };

  const renderTick = useCallback(
    (name: string) => {
      return (
        <div className="tickBox">
          {errorContainerNames.includes(name) ? (
            <AgxIconCircularWarning width={13} height={14} />
          ) : (
            <AgxIconCircularGreenTick width={13} height={14} />
          )}
        </div>
      );
    },
    [errorContainerNames]
  );

  const renderCompletionStatus = useCallback(() => {
    if (campaign?.address?.state === CampaignDetailModelState.QLD && campaignIsValid) {
      const completePanelsNum = pages.filter(
        (page) => !errorContainerNames.includes(page.name)
      ).length;
      const totalPanelsNum = pages.length;
      const allPanelsComplete = completePanelsNum === totalPanelsNum;
      return (
        <>
          <div className="circularProgress">
            {allPanelsComplete ? (
              <AgxIconCircularGreenTick width={26} height={27} />
            ) : (
              <AgxIconCircularIncomplete width={26} height={27} />
            )}
          </div>
          <AgxColumn>
            <AgxBodyText extraClasses="label">Blank Contract</AgxBodyText>
            {allPanelsComplete ? (
              <AgxBodyText extraClasses="completionStatus">
                <span className="completionStatus--complete">Completed</span>
              </AgxBodyText>
            ) : (
              <AgxBodyText extraClasses="completionStatus">
                <span className="completionStatus--complete">{completePanelsNum}</span>
                <span className="completionStatus--incomplete">/{totalPanelsNum} completed</span>
              </AgxBodyText>
            )}
          </AgxColumn>
        </>
      );
    }
    return <AgxBodyText large>Blank Contract</AgxBodyText>;
  }, [campaign?.address?.state, campaignIsValid, pages, errorContainerNames]);

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as unknown as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <FormPrompt hasUnsavedChanges={isFormDirty} />
      <AgxToast selector="#agxToast" toastState={toastState} />
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.PROPERTY_INFORMATION}
        isCampaigns={true}
        secondaryActions={<WithdrawListingAction />}
      >
        <AgxSpacer fill gap="sm" direction="vertical">
          {blankContractLoaderState && (
            <AgxAnimatedToggleLoaderOverlay
              background="transparent"
              label={
                blankContractLoaderState === AgxAnimatedToggleLoaderState.Complete
                  ? 'Complete'
                  : 'Generating blank contract...'
              }
              onComplete={handleBlankContractLoaderComplete}
              state={blankContractLoaderState}
            />
          )}
          <AgxSpacer id="subPageHeadingContainer" fill gap="spacing-2xl" direction="vertical">
            <AgxRow spaceBetween extraClasses={`${isGeneratingBlankContract ? 'disabledDiv' : ''}`}>
              <AgxRow largeGap>
                {renderCompletionStatus()}
                <AgxSwitch
                  dataTestId="liveSwitch"
                  id={'liveSwitch'}
                  onChange={toggleLiveBlankContract}
                  width={87}
                  dataOff="Offline"
                  dataOn="Live"
                  isChecked={campaign?.blankContractIsLive}
                  parentControlValue={true}
                />
              </AgxRow>
              <AgxRow largeGap>
                {showQldInfo && (
                  <AgxMultiOptionButton
                    dataTestId="btn-downloadContract"
                    id="downloadContract"
                    text="Download Contract"
                    medium
                    hollow
                    disabled={
                      !campaign?.blankSalesContract ||
                      !campaignIsValid ||
                      isGeneratingBlankContract ||
                      isLoadingFormConfig
                    }
                    options={fileDownloadOptions}
                    onClick={
                      fileDownloadOptions?.length === 1 ? fileDownloadOptions[0].onClick : undefined
                    }
                  />
                )}
                <AgxButton
                  text="Save Changes"
                  medium
                  primary
                  disabled={!campaignIsValid || isGeneratingBlankContract || isLoadingFormConfig}
                  onClick={() => {
                    if (!rexListing && isPendingRexListing === false) {
                      addChangedField(
                        RexDisconnectedModalFields.BlankContract,
                        'Upload/Replace Blank Contract Pdf'
                      );
                      openRexDisconnectedModal();
                    }
                    onSaveFormValues();
                  }}
                  dataTestId="btn-saveChanges"
                />
              </AgxRow>
            </AgxRow>
            <AgxDivider variant="horizontal" pushMargin="spacing-3xl" />
          </AgxSpacer>
          <AgxRow veryLargeGap extraClasses={!showQldInfo ? 'column' : ''}>
            {campaignIsValid && showQldInfo ? (
              <div className="leftMenuContainer">
                <AgxColumn largeGap>
                  {pages?.map((panel) => (
                    <div key={slugify(panel.name)} className="leftMenu">
                      {renderTick(panel.name)}
                      <a href={`#${slugify(panel.name)}`} className="leftMenuLinkStyle">
                        <AgxBodyText small extraClasses="strong">
                          {panel.name}
                        </AgxBodyText>
                      </a>
                    </div>
                  ))}
                </AgxColumn>
              </div>
            ) : (
              <></>
            )}
            <div id="panelContainer">
              {!campaignIsValid ? (
                <AgxLabel extraClasses="loadingText_campaigns">
                  This campaign is not ready for blank contract generation
                </AgxLabel>
              ) : !isLoadingFormConfig && campaign ? (
                <AgxColumn extraLargeGap>
                  {pages?.map((panel) => {
                    return (
                      <PanelElementRenderer
                        key={slugify(panel.name)}
                        panel={panel}
                        formValues={formValues}
                        getAuthToken={getAuthToken}
                        onChange={onChange}
                        campaign={campaign}
                        validate={validate}
                        onSellerDisclosureComplete={onSellerDisclosureComplete}
                      />
                    );
                  })}
                </AgxColumn>
              ) : (
                <></>
              )}
            </div>
          </AgxRow>
        </AgxSpacer>

        {openErrorModal ? (
          <ErrorModal errorList={submitFormErrors} setShowModal={setOpenErrorModal} />
        ) : (
          <></>
        )}

        {openConfirmationModal ? (
          <SetBlankContractConfirmModal
            setShowModal={setOpenConfirmationModal}
            onConfirm={onSubmit}
            onNotifyAgentToggle={onNotifyAgentToggle}
          />
        ) : (
          <></>
        )}

        {rexDisconnectedModal}
      </ContentLayout>
    </PageLayout>
  );
};

export default ManageCampaign;
